/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { getFullNameWithTFL } from '@medflex/shared-utils';
import { Link } from 'react-router-dom';
import { Sources, RequestTypes, PhoneStatus, RequestStatus } from '@medflex/shared-types';
import { useTranslation } from 'react-i18next';
import { PhoneIcon, DocumentIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { InquiryPropType } from './PropTypes';
import AssigneeAvatar from './AssigneeAvatar';
import InquiryStatusLabel, { InquiryStatusLabelVariants } from './InquiryStatusLabel';
import {
  IconWidgetCalendar,
  IconWidgetLink,
  IconWidgetPrescription,
  IconWidgetQuestion,
  IconWidgetReferral,
  IconWidgetResultsInquiry,
} from '../Icons';
import Badge, { KINDS } from '../Badge';
import PhoneStatusBadge, { Variants } from './PhoneStatusBadge';

const icons = {
  appointment: IconWidgetCalendar,
  findings_inquiry: IconWidgetResultsInquiry,
  referral: IconWidgetReferral,
  request_follow_up_prescription: IconWidgetPrescription,
  general_inquiry: IconWidgetQuestion,
  connect_to_physician: IconWidgetLink,
  phone: PhoneIcon,
  document: DocumentIcon,
  message: EnvelopeIcon,
};

const HeaderSubtitle = ({ inquiry }) => {
  const { t } = useTranslation('components');

  const { requestType, appointmentAction, diagnosisRequestType, source } = inquiry;

  if (source === Sources.PHONE) return null;

  switch (requestType) {
    case RequestTypes.APPOINTMENT:
      return appointmentAction ? (
        <div className="text-sm">
          {t(`PamInquiry.inquiryTypes.${requestType}.subtitle.${appointmentAction}`)}
        </div>
      ) : null;
    case RequestTypes.FINDINGS_INQUIRY:
      return diagnosisRequestType ? (
        <div className="text-sm">
          {t(`PamInquiry.inquiryTypes.${requestType}.subtitle.${diagnosisRequestType}`)}
        </div>
      ) : null;
    default:
      return null;
  }
};

const AssigneeAndStatusInfo = ({ inquiry, isPatient = true }) => {
  const { t } = useTranslation('components');
  const { assignee, source, phoneStatus, status: inquiryStatus, isForwardedCall } = inquiry;
  const showPhoneStatus =
    source === Sources.PHONE &&
    [PhoneStatus.UNCOMPLETED, PhoneStatus.CANCELED].includes(phoneStatus);

  return (
    <div className="flex items-center gap-2">
      {assignee && <AssigneeAvatar assignee={assignee} />}

      {isForwardedCall && (
        <Badge kind={KINDS.SECONDARY}>
          <span className="max-w-[144px] truncate">{t('PamInquiry.forwarded')}</span>
        </Badge>
      )}
      {showPhoneStatus && <PhoneStatusBadge phoneStatus={phoneStatus} variant={Variants.FULL} />}
      {!(showPhoneStatus && [RequestStatus.OPEN, RequestStatus.PROGRESS].includes(inquiryStatus)) &&
        (inquiry.statusChanged?.updatedAt ? (
          <InquiryStatusLabel
            variant={InquiryStatusLabelVariants.TEXT}
            isPatient={isPatient}
            status={inquiry.status}
            statusChangedBy={
              inquiry.statusChanged.user
                ? getFullNameWithTFL(inquiry.statusChanged.user)
                : t('global:deleted_user')
            }
            statusChangedAt={inquiry.statusChanged.updatedAt}
            hasTooltip={!isPatient}
          />
        ) : (
          <InquiryStatusLabel
            isPatient={isPatient}
            status={inquiry.status}
            variant={InquiryStatusLabelVariants.TEXT}
          />
        ))}
    </div>
  );
};

AssigneeAndStatusInfo.propTypes = {
  inquiry: InquiryPropType.isRequired,
  isPatient: PropTypes.bool,
};

const InquiryHeader = ({ inquiry, link = '', isPatient = true, isDeleted = false }) => {
  const { t } = useTranslation('components');
  const Icon = icons[inquiry.requestType];
  const cn = classNames([
    'flex',
    'items-center',
    'space-x-4',
    'p-3',
    'pr-2',
    isDeleted ? 'bg-slate-100' : 'bg-blue-100',
  ]);

  const header = (
    <div className={cn}>
      <Icon className="h-8 w-8" />
      <div className="flex flex-1 items-center justify-between">
        <div>
          <div className="text-lg font-semibold">
            {t(`PamInquiry.inquiryTypes.${inquiry.requestType}.title`)}
          </div>
          <HeaderSubtitle inquiry={inquiry} />
        </div>
        <div className="flex items-center">
          <AssigneeAndStatusInfo inquiry={inquiry} isPatient={isPatient} />
        </div>
      </div>
    </div>
  );

  if (link) {
    return <Link to={link}>{header}</Link>;
  }

  return header;
};

InquiryHeader.propTypes = {
  inquiry: InquiryPropType.isRequired,
  link: PropTypes.string,
  isPatient: PropTypes.bool,
  isDeleted: PropTypes.bool,
};

HeaderSubtitle.propTypes = {
  inquiry: InquiryPropType.isRequired,
};

export default InquiryHeader;
